import { PropsWithChildren } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { formatDate } from '@/core/helpers';
import { APIEventsEnum, useSendEvent } from '@/hooks';

import SendIcon from '@/icons/sent.svg?react';
import UploadIcon from '@/icons/upload_arrow.svg?react';
import { useModal } from '../Modal/useModal';
import { useMU } from '@/context/useMU';
import { PlaceToUploadEnum } from '@/core/enums';
import ManualUploadFile from '../ManualDataUploader/ManualUploadFile';
import Button from '../Button';
import FileUploader from '../FileUploader/FileUploader';
import { ACCEPTED_EXTS } from '../ManualDataUploader/ManualUploader.helpers';
import { StopMUModal } from './StopMUModal';
interface Props {
  noDataNeeded: boolean;
  hasUploadedMUFile: boolean;
  isMUNeeded: boolean;
  unitDate: string;
  className?: string;
  isProcessingFiles: boolean;
}

const ManualFileUploader = ({
  unitDate,
  noDataNeeded,
  isMUNeeded,
  hasUploadedMUFile,
  isProcessingFiles,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation();
  const { sendEvent } = useSendEvent();

  const { isModalOpened, closeModal, openModal } = useModal();

  const {
    onSelectFiles,
    onSubmitFiles,
    isUploadingFiles,
    filesToUpload,
    onDeleteFileToUpload,
    placeToUpload,
  } = useMU();

  const isFullWidthUploaderShown = hasUploadedMUFile;

  return (
    <>
      <div className='space-y-2'>
        {(!noDataNeeded || isMUNeeded) && (
          <>
            <div className='flex items-center justify-between'>
              {!isFullWidthUploaderShown && (
                <div className='max-w-[590px]'>
                  <Trans
                    i18nKey='Upload all transactions for {{month}} or confirm that <button> no manual data is needed </button> for this period'
                    values={{ month: formatDate(new Date(unitDate), 'MMMM') }}
                    components={{
                      button: (
                        <Button
                          onClick={openModal}
                          styling='naked-blue'
                          className='!font-semibold'
                        />
                      ),
                    }}
                  />
                </div>
              )}
              <div className='flex flex-shrink-0 flex-grow items-center justify-end gap-x-4'>
                {isProcessingFiles ? (
                  <Button
                    disabled
                    className={`Button flex cursor-pointer items-center ${
                      isFullWidthUploaderShown ? '!w-full justify-center' : ''
                    }`}
                  >
                    <UploadIcon className='mr-2 [&>path]:fill-white' />
                    <span>{t('Upload file')}</span>
                  </Button>
                ) : (
                  <FileUploader
                    onSelect={onSelectFiles(PlaceToUploadEnum.ToDo)}
                    name='manual-upload-file'
                    accept={ACCEPTED_EXTS}
                    className={`Button flex cursor-pointer items-center ${
                      isFullWidthUploaderShown
                        ? 'Button--outline !w-full justify-center'
                        : ''
                    }`}
                    multiple={false}
                  >
                    <UploadIcon
                      className={`mr-2 ${
                        isFullWidthUploaderShown ? '' : '[&>path]:fill-white'
                      }`}
                    />
                    <span>{t('Upload file')}</span>
                  </FileUploader>
                )}
              </div>
            </div>
            <form
              onSubmit={(e) => {
                sendEvent(APIEventsEnum.files_uploaded_from_turnover_todo, {
                  block:
                    'customer uploaded files from the turnover todo section',
                });
                onSubmitFiles(e);
              }}
            >
              {!!filesToUpload?.length &&
                placeToUpload === PlaceToUploadEnum.ToDo && (
                  <div className='pt-2'>
                    {filesToUpload.map((f) => (
                      <ManualUploadFile
                        className='mb-2 flex-grow'
                        onDeleteFile={onDeleteFileToUpload(f.name)}
                        file={f}
                        disabled={false}
                        key={f.name}
                        isUploading={isUploadingFiles}
                      />
                    ))}
                  </div>
                )}
              {!!filesToUpload?.length &&
                placeToUpload === PlaceToUploadEnum.ToDo &&
                !isUploadingFiles && (
                  <Button
                    className='mt-4 flex items-center !py-1 !pl-4 !pr-6'
                    type='submit'
                    disabled={isUploadingFiles}
                    data-testid='manual-upload-submit'
                  >
                    <SendIcon className='mr-2' />
                    <span>{t('Submit file')}</span>
                  </Button>
                )}
            </form>
          </>
        )}

        {!isMUNeeded && noDataNeeded && (
          <div>
            {t(
              'You can upload corrected invoices or turnover data for past periods, if requested.'
            )}
          </div>
        )}
      </div>
      <StopMUModal isModalOpened={isModalOpened} closeModal={closeModal} />
    </>
  );
};

export default ManualFileUploader;
